import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack } from '@mui/material';
import { t } from 'i18next';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { calcTotalRackFramesDepth } from '@/modules/common/helpers/rack';
import { NumUtils } from '@/modules/common/utils';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { PropertiesPopup } from '@/modules/common/components/PropertiesPopup';
import { PropertiesTitle } from '@/modules/common/components/PropertiesTitle';
import { AccordionPreferenceIconButton } from '@/modules/common/components/AccordionPreferenceIconButton';
import { useDebouncedCallback } from '@/modules/common/hooks';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { areasToFlatCollection, isRackingArea } from '@/modules/floorplanDesign/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { isAtLeast1 } from '@/modules/floorplanDesign/helpers/minItems';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

enum InputName {
  BEAM_HEIGHT = 'beam_height',
  CLEARANCE_TOP = 'clearance_top',
  CLEARANCE_IN_BETWEEN = 'clearance_in_between',
  CLEARANCE_SIDE = 'clearance_side',
  FIRST_SHELF_HEIGHT = 'first_shelf_height',
}

const mapInputToImage = (inputName: InputName | null) => {
  switch (inputName) {
    case InputName.CLEARANCE_SIDE:
      return '/racking/side-margin.png';
    case InputName.CLEARANCE_TOP:
      return '/racking/top-margin.png';
    case InputName.CLEARANCE_IN_BETWEEN:
      return '/racking/gap-between-loads.png';
    case InputName.BEAM_HEIGHT:
      return '/racking/beam.png';
    case InputName.FIRST_SHELF_HEIGHT:
      return '/racking/first-shelf-height.png';
    default:
      return '/racking/default.png';
  }
};

const getSelectedRackingArea = (selectedAreas: ReturnType<typeof useReadSelectedAreas>) => {
  const areas = areasToFlatCollection(selectedAreas);
  if (areas.length !== 1) return null;

  const area = areas[0];
  if (!isRackingArea(area)) return null;

  return area;
};

export const RackSpec = () => {
  const { getLoadTypes } = useFloorplanDesignStore();
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const [focusedInputName, setFocusedInputName] = useState(null);
  const [image, setImage] = useState('/racking/default.png');
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const selectedAreas = useReadSelectedAreas();
  const area = useMemo(() => getSelectedRackingArea(selectedAreas), [selectedAreas]);
  const rackSpec = area?.rackSpec;

  const { callback: setImageDebounced } = useDebouncedCallback(
    (value: string) => setImage(value),
    50,
  );

  useEffect(() => {
    setImageDebounced(mapInputToImage(focusedInputName));
  }, [focusedInputName, setImageDebounced]);

  const handleSetAccordionOpen = useCallback((expanded: boolean) => {
    setAccordionOpen(expanded);
  }, []);

  const onPopupOpen = useCallback(() => {
    setPopupOpen(true);
    setAccordionOpen(true);
  }, []);

  const onPopupClose = useCallback(() => {
    setPopupOpen(false);
  }, []);

  const onFocus = useCallback(
    (evt) => {
      setFocusedInputName(evt.target.name);
    },
    [setFocusedInputName],
  );

  const onBlur = useCallback(() => {
    setFocusedInputName(null);
  }, [setFocusedInputName]);

  const loadTypeSize = useMemo(() => {
    const loadType = getLoadTypes().find((lt) => lt.id === rackSpec?.loadTypes[0].loadTypeId);

    return loadType?.width ?? 0;
  }, [getLoadTypes, rackSpec?.loadTypes]);

  const onLevelsChange = useCallback(
    (value: number) => {
      if (!rackSpec) return;

      const newArray = Array.from({ length: value }, (_, i) => rackSpec.shelfHeights[0] + i * 1);

      if (!isAtLeast1(newArray)) return;

      fddmSvc.updateArea({
        ...area,
        rackSpec: { ...rackSpec, shelfHeights: newArray },
      });
    },
    [area, fddmSvc, rackSpec],
  );

  const onBeamHeightChange = useCallback(
    (valueMm: number) => {
      if (!rackSpec) return;

      fddmSvc.setPreference('rackingArea.rackSpec.beamHeight', NumUtils.mmToMeters(valueMm));
      fddmSvc.updateArea({
        ...area,
        rackSpec: { ...rackSpec, beamHeight: NumUtils.mmToMeters(valueMm) },
      });
    },
    [area, fddmSvc, rackSpec],
  );

  const onClearanceSideChange = useCallback(
    (valueMm: number) => {
      if (!rackSpec) return;

      const valueSanitized = NumUtils.mmToMeters(valueMm);

      const rackingLoadTypeIds = new Set(rackSpec.loadTypes.map((loadType) => loadType.loadTypeId));
      const rackingLoadTypes = useFloorplanDesignStore
        .getState()
        .setup.loadTypes.filter((loadType) => rackingLoadTypeIds.has(loadType.id));
      const loadWidth = Math.max(...rackingLoadTypes.map((lt) => lt.width));
      const { clearanceInBetween, numColumnsInBay } = rackSpec;
      const updatedBeamLength =
        numColumnsInBay * loadWidth +
        2 * valueSanitized +
        (numColumnsInBay - 1) * clearanceInBetween;
      const currentPostWidth = (rackSpec.bayLength - rackSpec.beamLength) / 2;
      const updatedBayLength = updatedBeamLength + 2 * currentPostWidth;

      fddmSvc.setPreference('rackingArea.rackSpec.clearanceSide', valueSanitized);
      fddmSvc.setPreference('rackingArea.rackSpec.beamLength', updatedBeamLength);
      fddmSvc.setPreference('rackingArea.rackSpec.bayLength', updatedBayLength);
      fddmSvc.updateArea({
        ...area,
        rackSpec: {
          ...rackSpec,
          clearanceSide: valueSanitized,
          beamLength: updatedBeamLength,
          bayLength: updatedBayLength,
        },
      });
    },
    [area, fddmSvc, rackSpec],
  );

  const onClearanceTopChange = useCallback(
    (valueMm: number) => {
      if (!rackSpec) return;

      fddmSvc.setPreference('rackingArea.rackSpec.clearanceTop', NumUtils.mmToMeters(valueMm));
      fddmSvc.updateArea({
        ...area,
        rackSpec: { ...rackSpec, clearanceTop: NumUtils.mmToMeters(valueMm) },
      });
    },
    [area, fddmSvc, rackSpec],
  );

  const onClearanceInBetweenChange = useCallback(
    (valueMm: number) => {
      if (!rackSpec) return;

      const valueSanitized = NumUtils.mmToMeters(valueMm);

      const rackingLoadTypeIds = new Set(rackSpec.loadTypes.map((loadType) => loadType.loadTypeId));
      const rackingLoadTypes = useFloorplanDesignStore
        .getState()
        .setup.loadTypes.filter((loadType) => rackingLoadTypeIds.has(loadType.id));
      const loadWidth = Math.max(...rackingLoadTypes.map((lt) => lt.width));
      const { clearanceSide, numColumnsInBay } = rackSpec;
      const updatedBeamLength =
        numColumnsInBay * loadWidth + 2 * clearanceSide + (numColumnsInBay - 1) * valueSanitized;
      const currentPostWidth = (rackSpec.bayLength - rackSpec.beamLength) / 2;
      const updatedBayLength = updatedBeamLength + 2 * currentPostWidth;

      fddmSvc.setPreference('rackingArea.rackSpec.clearanceInBetween', valueSanitized);
      fddmSvc.setPreference('rackingArea.rackSpec.beamLength', updatedBeamLength);
      fddmSvc.setPreference('rackingArea.rackSpec.bayLength', updatedBayLength);
      fddmSvc.updateArea({
        ...area,
        rackSpec: {
          ...rackSpec,
          clearanceInBetween: valueSanitized,
          beamLength: updatedBeamLength,
          bayLength: updatedBayLength,
        },
      });
    },
    [area, fddmSvc, rackSpec],
  );

  const onNumColumnsInBayChange = useCallback(
    (value: number) => {
      if (!rackSpec) return;

      const rackingLoadTypeIds = new Set(rackSpec.loadTypes.map((loadType) => loadType.loadTypeId));
      const rackingLoadTypes = useFloorplanDesignStore
        .getState()
        .setup.loadTypes.filter((loadType) => rackingLoadTypeIds.has(loadType.id));
      const loadWidth = Math.max(...rackingLoadTypes.map((lt) => lt.width));
      const { clearanceSide, clearanceInBetween } = rackSpec;
      const updatedBeamLength =
        value * loadWidth + 2 * clearanceSide + (value - 1) * clearanceInBetween;
      const currentPostWidth = (rackSpec.bayLength - rackSpec.beamLength) / 2;
      const updatedBayLength = updatedBeamLength + 2 * currentPostWidth;

      fddmSvc.setPreference('rackingArea.rackSpec.numColumnsInBay', value);
      fddmSvc.setPreference('rackingArea.rackSpec.beamLength', updatedBeamLength);
      fddmSvc.setPreference('rackingArea.rackSpec.bayLength', updatedBayLength);
      fddmSvc.updateArea({
        ...area,
        rackSpec: {
          ...rackSpec,
          numColumnsInBay: value,
          beamLength: updatedBeamLength,
          bayLength: updatedBayLength,
        },
      });
    },
    [area, fddmSvc, rackSpec],
  );

  const onNumStationsInDepthChange = useCallback(
    (value: number) => {
      if (!rackSpec) return;

      const bayDepth = calcTotalRackFramesDepth(
        loadTypeSize,
        value,
        rackSpec.clearanceDepth + rackSpec.overhangFront + rackSpec.overhangBack,
      );

      fddmSvc.setPreference('rackingArea.rackSpec.numStationsInDepth', value);
      fddmSvc.updateArea({
        ...area,
        rackSpec: { ...rackSpec, numStationsInDepth: value, bayDepth },
      });
    },
    [loadTypeSize, area, fddmSvc, rackSpec],
  );

  const onFirstShelfHeightChange = useCallback(
    (value: number) => {
      if (!rackSpec) return;

      const newArray = Array.from(
        { length: rackSpec.shelfHeights.length },
        (_, i) => NumUtils.mmToMeters(value) + i * 1,
      );

      if (!isAtLeast1(newArray)) return;

      fddmSvc.updateArea({
        ...area,
        rackSpec: { ...rackSpec, shelfHeights: newArray },
      });
    },
    [area, fddmSvc, rackSpec],
  );

  if (!rackSpec || !loadTypeSize) return null;

  return (
    <>
      <Accordion
        title={t<string>('interface:properties.area.rack_spec.label', 'Racking Spec')}
        unmountOnExit
        expanded={accordionOpen}
        onChange={handleSetAccordionOpen}
        defaultExpanded
        sx={{ px: 2 }}
        ref={panelRef}
        afterIcon={
          <AccordionPreferenceIconButton
            tooltip={t('interface:properties.area.rack.racking_preferences')}
            onAdd={onPopupOpen}
            tooltipWhenDisabled={false}
          />
        }
      >
        <Stack direction='column' spacing={1}>
          <PropertiesLabel labelRatio={60} i18nkey='interface:properties.area.rack_spec.levels'>
            <NumberInput
              value={rackSpec.shelfHeights.length}
              sx={{ width: '100%' }}
              minValue={1}
              onChange={onLevelsChange}
            />
          </PropertiesLabel>

          <PropertiesLabel
            labelRatio={60}
            i18nkey='interface:properties.area.rack_spec.num_Columns_In_Bay'
          >
            <NumberInput
              value={rackSpec.numColumnsInBay}
              sx={{ width: '100%' }}
              onChange={onNumColumnsInBayChange}
              minValue={1}
            />
          </PropertiesLabel>

          <PropertiesLabel
            labelRatio={60}
            i18nkey='interface:properties.area.rack_spec.num_stations_in_depth'
          >
            <NumberInput
              value={rackSpec.numStationsInDepth}
              sx={{ width: '100%' }}
              onChange={onNumStationsInDepthChange}
              minValue={1}
            />
          </PropertiesLabel>
        </Stack>
      </Accordion>
      <PropertiesPopup
        anchor={panelRef}
        open={popupOpen}
        onClose={onPopupClose}
        header={
          <PropertiesTitle
            fontSize={14}
            fontWeight={400}
            value={t('interface:properties.area.rack.racking_preferences')}
          />
        }
        sx={{
          width: '320px',
          height: 'auto',
        }}
      >
        <Stack gap={0}>
          <Stack alignItems='center'>
            {image && <img src={image} alt='' width={203} height={223} />}
          </Stack>

          <Stack gap={1} px={2}>
            <PropertiesLabel
              labelRatio={60}
              i18nkey='interface:properties.area.rack_spec.clearance_side'
            >
              <NumberInput
                value={NumUtils.metersToMmRounded(rackSpec.clearanceSide)}
                onChange={onClearanceSideChange}
                name={InputName.CLEARANCE_SIDE}
                sx={{ width: '100%' }}
                onBlur={onBlur}
                onFocus={onFocus}
                unit='mm'
                minValue={0}
              />
            </PropertiesLabel>
            <PropertiesLabel
              labelRatio={60}
              i18nkey='interface:properties.area.rack_spec.clearance_in_between'
            >
              <NumberInput
                value={NumUtils.metersToMmRounded(rackSpec.clearanceInBetween)}
                onChange={onClearanceInBetweenChange}
                name={InputName.CLEARANCE_IN_BETWEEN}
                sx={{ width: '100%' }}
                onBlur={onBlur}
                onFocus={onFocus}
                unit='mm'
                minValue={0}
              />
            </PropertiesLabel>
            <PropertiesLabel
              labelRatio={60}
              i18nkey='interface:properties.area.rack_spec.clearance_top'
            >
              <NumberInput
                value={NumUtils.metersToMmRounded(rackSpec.clearanceTop)}
                onChange={onClearanceTopChange}
                name={InputName.CLEARANCE_TOP}
                sx={{ width: '100%' }}
                onBlur={onBlur}
                onFocus={onFocus}
                unit='mm'
                minValue={0}
              />
            </PropertiesLabel>
            <PropertiesLabel
              labelRatio={60}
              i18nkey='interface:properties.area.rack_spec.beam_height'
            >
              <NumberInput
                value={NumUtils.metersToMmRounded(rackSpec.beamHeight)}
                onChange={onBeamHeightChange}
                name={InputName.BEAM_HEIGHT}
                sx={{ width: '100%' }}
                onBlur={onBlur}
                onFocus={onFocus}
                unit='mm'
                minValue={1}
              />
            </PropertiesLabel>
            <PropertiesLabel
              labelRatio={60}
              i18nkey='interface:properties.area.rack.first_shelf_height'
            >
              <NumberInput
                value={NumUtils.metersToMmRounded(rackSpec.shelfHeights[0])}
                onChange={onFirstShelfHeightChange}
                name={InputName.FIRST_SHELF_HEIGHT}
                sx={{ width: '100%' }}
                onBlur={onBlur}
                onFocus={onFocus}
                unit='mm'
                minValue={0}
              />
            </PropertiesLabel>
          </Stack>
        </Stack>
      </PropertiesPopup>
    </>
  );
};
